'use client';

import { Composite } from '@floating-ui/react';
import { forwardRef, useCallback, useMemo, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { useChildrenOfType } from '../../hooks/use-children-of-type';
import { useControllableValue } from '../../hooks/use-controllable-value';
import { Slot } from '../slot';
import {
  AccordionRootActionsContext,
  AccordionRootContext,
  AccordionRootStateContext,
} from './context/accordion-root.context';
import { accordionRootStyles } from './styles/accordion-root.styles';
import type { AccordionRootPropsInterface } from './types/accordion-root.types';

/**
 * @subcomponents ['Accordion.Item', 'Accordion.Header', 'Accordion.Panel']
 * @since 8.43.0
 */

export const AccordionRoot = forwardRef<HTMLUListElement, AccordionRootPropsInterface>(
  (props: AccordionRootPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      children,
      className,
      css: cssProp,
      defaultExpanded,
      expanded,
      onExpandedChange,
      onPanelFocus,
      type = 'multiple',
      shouldAwaitInteractionResponse = true,
      ...rest
    } = props;

    const [expandedState, setExpandedState] = useControllableValue<
      string | Array<string> | undefined
    >({
      controlledValue: expanded,
      uncontrolledValue: defaultExpanded,
      isControlled: 'expanded' in props,
      isUncontrolled: 'defaultExpanded' in props,
      onChange: (newExpanded) => {
        if (newExpanded !== undefined) {
          onExpandedChange?.(newExpanded);
        }
      },
      componentName: 'Accordion.Root',
      controlledPropName: 'expanded',
      uncontrolledPropName: 'defaultExpanded',
    });

    const toggleExpanded = useCallback(
      (value: string) => {
        setExpandedState((prevState) => {
          if (!prevState) return value;

          if (type === 'single') {
            return value === prevState ? '' : value;
          }

          if (typeof prevState === 'string') {
            return prevState === value ? [] : [prevState, value];
          }

          if (Array.isArray(prevState)) {
            if (prevState.includes(value)) {
              return prevState.filter((item) => item !== value);
            }
            return [...prevState, value];
          }
        });
      },
      [setExpandedState, type],
    );

    const accordionItems = useChildrenOfType(children, 'Accordion.Item');

    const [activeIndex, setActiveIndex] = useState(() => {
      return expandedState
        ? accordionItems.findIndex((accordionItem) => accordionItem.props.value === expandedState)
        : 0;
    });

    const onFocusChange = useCallback(
      (index: number) => {
        setActiveIndex(index);
        if (accordionItems?.[index]?.props.value) {
          onPanelFocus?.(accordionItems?.[index]?.props.value);
        }
      },
      [accordionItems, onPanelFocus],
    );

    const Component = asChild ? Slot : 'ul';

    const styles = css(accordionRootStyles, cssProp);

    const expandedValues = useMemo(() => {
      if (!Array.isArray(expandedState) || expandedState.length <= 1) {
        return undefined;
      }

      return new Set(expandedState);
    }, [expandedState]);

    const stateContextValue = useMemo(
      () => ({
        expanded: expandedState,
        expandedValues,
      }),
      [expandedState, expandedValues],
    );

    const actionsContextValue = useMemo(
      () => ({
        toggleExpanded,
        onExpandedChange,
        onPanelFocus,
        shouldAwaitInteractionResponse,
      }),
      [toggleExpanded, onExpandedChange, onPanelFocus, shouldAwaitInteractionResponse],
    );

    const contextValue = useMemo(
      () => ({
        expanded: expandedState,
        ...actionsContextValue,
      }),
      [expandedState, actionsContextValue],
    );

    /**
     * Legacy combined context kept to avoid breaking the public AccordionRootContext export.
     * Accordion internals should use the split state/actions contexts; remove in v11.
     */
    return (
      <AccordionRootContext.Provider value={contextValue}>
        <AccordionRootStateContext.Provider value={stateContextValue}>
          <AccordionRootActionsContext.Provider value={actionsContextValue}>
            {/* Intentionally not allowing user overrides for Composite */}
            <Composite loop activeIndex={activeIndex} onNavigate={onFocusChange}>
              <Component
                className={cx('c11n', 'c11n_accordion__root', styles, className)}
                data-c11n-component="Accordion.Root"
                data-c11n-version={__C11N_VERSION__}
                ref={forwardedRef}
                {...rest}
              >
                {children}
              </Component>
            </Composite>
          </AccordionRootActionsContext.Provider>
        </AccordionRootStateContext.Provider>
      </AccordionRootContext.Provider>
    );
  },
);

AccordionRoot.displayName = 'Accordion.Root';
